import { useState } from 'react';
import { PATH_DASHBOARD } from 'src/Router/paths';
import { NavActionButton } from 'src/features/Navbar/NavActionButton/NavActionButton';
import { NavItem } from 'src/features/Navbar/NavItem';
import { BoardAvatar } from 'src/features/board/BoardAvatar';
import useActiveLink from 'src/hooks/useActiveLink';
import { useBoardConfigDialog } from 'src/sections/@dashboard/board/BoardConfigDialog';

type ITypes = 'board' | 'table' | 'view';

type ISubType = {
  title: string;
  icon: string;
  subList?: string;
  subType?: ITypes;
  action?: React.ReactNode;
};

const pathTypes = {
  ['board']: (boardId: string, tableId: string, viewId: string) =>
    PATH_DASHBOARD.board.view(boardId),
  ['table']: (boardId: string, tableId: string, viewId: string) =>
    PATH_DASHBOARD.board.table({ boardId, tableId }),
  ['view']: (boardId: string, tableId: string, viewId: string) =>
    PATH_DASHBOARD.board.tableView({ boardId, tableId, viewId }),
};

type Props = {
  depth: number;
  type: ITypes;
  item: any;
  boardId: string;
  tableId?: string;
  viewId?: string;
};

export const BoardNavSubItem = ({
  depth,
  type,
  item,
  boardId,
  tableId,
  viewId,
}: Props) => {
  const { showBoardConfig } = useBoardConfigDialog();

  const subTypes: Record<ITypes, ISubType> = {
    ['board']: {
      title: 'name',
      icon: 'tabler:database',
      subList: 'tables',
      subType: 'table',
      action: (
        <NavActionButton
          icon="tabler:settings"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            showBoardConfig({ boardId, shareMode: false });
          }}
          size={16}
          color="default"
        />
      ),
    },
    ['table']: {
      title: 'name',
      icon: 'tabler:table',
      subList: 'views',
      subType: 'view',
    },
    ['view']: {
      title: 'title',
      icon: item.type === 'LIST' ? 'tabler:list' : 'tabler:layout-kanban',
    },
  };

  const itemType = subTypes[type];
  const subList: any[] = itemType.subList ? item[itemType.subList] : [];
  const path = pathTypes[type](boardId, tableId!, viewId!);

  const { active } = useActiveLink(path);

  const [isOpen, setIsOpen] = useState(active);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        paddingLeft: !!depth ? '8px' : 0,
      }}
    >
      <NavItem
        key={item._id}
        path={path}
        depth={depth}
        icon={<BoardAvatar icon={itemType.icon} emoji={item.icon} />}
        isTreeView={true}
        hasSubNav={!!subList?.length}
        toggleSubNav={() => setIsOpen((prevState) => !prevState)}
        isSubNavOpen={isOpen}
        action={itemType.action}
        linkActiveMatchChildren
        onClick={() => setIsOpen(true)}
        sx={{ mb: 0.5 }}
      >
        {item[subTypes[type].title]}
      </NavItem>
      {!!subList.length && isOpen && (
        <>
          {subList.map((subItem) => (
            <BoardNavSubItem
              key={subItem._id}
              depth={depth + 1}
              type={itemType.subType!}
              item={subItem}
              boardId={type === 'board' ? item._id : boardId}
              tableId={itemType.subType === 'table' ? subItem._id : tableId}
              viewId={itemType.subType === 'view' ? subItem._id : viewId}
            />
          ))}
        </>
      )}
    </div>
  );
};
